"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface Citation {
  quote: string;
  source: string;
  index: number | null;
  number: number;
  color: string;
}

interface CitationContextValue {
  activeCriterionId: string | null;
  citations: Citation[];
  criterionColor: string | null;
  activate: (
    criterionId: string,
    position: number,
    evidence: { quote: string; source: string; index?: number | null }[],
  ) => void;
  deactivate: () => void;
}

const CITATION_PALETTE = [
  "#7c9df5",
  "#c4a4f7",
  "#7dd3c0",
  "#f5a6c0",
  "#9bb8ed",
  "#c5d486",
  "#e6b07e",
  "#8dc6f0",
];

const CitationContext = createContext<CitationContextValue | null>(null);

export function CitationProvider({ children }: { children: ReactNode }) {
  const [activeCriterionId, setActiveCriterionId] = useState<string | null>(
    null,
  );
  const [citations, setCitations] = useState<Citation[]>([]);
  const [criterionColor, setCriterionColor] = useState<string | null>(null);

  const activate = useCallback(
    (
      criterionId: string,
      position: number,
      evidence: { quote: string; source: string; index?: number | null }[],
    ) => {
      const color =
        CITATION_PALETTE[(position - 1) % CITATION_PALETTE.length];
      setActiveCriterionId(criterionId);
      setCriterionColor(color);
      setCitations(
        evidence.map((ev, i) => ({
          quote: ev.quote,
          source: ev.source,
          index: ev.index ?? null,
          number: i + 1,
          color,
        })),
      );
    },
    [],
  );

  const deactivate = useCallback(() => {
    setActiveCriterionId(null);
    setCitations([]);
    setCriterionColor(null);
  }, []);

  const value = useMemo<CitationContextValue>(
    () => ({
      activeCriterionId,
      citations,
      criterionColor,
      activate,
      deactivate,
    }),
    [activeCriterionId, citations, criterionColor, activate, deactivate],
  );

  return (
    <CitationContext.Provider value={value}>
      {children}
    </CitationContext.Provider>
  );
}

export function useCitationContext() {
  return useContext(CitationContext);
}
